import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  Snowflake, Leaf, Plus, Trash, X, Check, MapPin, PencilSimple,
  CaretUp, CaretDown, ClockCounterClockwise, CloudSnow, Warning, UploadSimple,
  NavigationArrow, Path, Printer, ArrowSquareOut,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminSeasonal — snow removal + lawn care vendor + route tracker.
 * Route: /admin/seasonal
 *
 * Two tabs (Snow / Lawn). Each shows a list of "services" (a vendor +
 * coverage + route). Click a service to expand — inline coverage editor,
 * drag-to-reorder route, and a "Log Visit Today" button per stop.
 */
export default function AdminSeasonal() {
  const [tab, setTab] = useState("snow");   // snow | lawn
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [vendorsKnown, setVendorsKnown] = useState(["LCV", "Lawn Guru", "In-House"]);

  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  // React 18 StrictMode + tab switches can fire this twice, so we guard
  // against stale responses clobbering the current tab (e.g. a slow "snow"
  // reply landing after "lawn" and blanking the list).
  const loadReq = useRef(0);
  const load = () => {
    const wantTab = tab;
    const reqId = ++loadReq.current;
    setLoading(true);
    axios.get(`${BASE}/api/seasonal-services`, { ...auth(), params: { service_type: wantTab } })
      .then(r => {
        if (reqId !== loadReq.current) return;   // stale
        setServices(r.data.services || []);
        setVendorsKnown(r.data.vendors_known || vendorsKnown);
      })
      .catch(e => { if (reqId === loadReq.current) toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to load services")); })
      .finally(() => { if (reqId === loadReq.current) setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = services.filter(s => s.service_type === tab);
  const Icon = tab === "snow" ? Snowflake : Leaf;
  const brand = tab === "snow" ? "cyan" : "emerald";

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-seasonal-page">
        <div className="flex items-center gap-3">
          <Icon weight="fill" size={30} className={tab === "snow" ? "text-cyan-500" : "text-emerald-600"}/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Seasonal Services
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Snow removal + lawn care — vendors, routes, and per-visit logs
            </div>
          </div>
          <button onClick={() => setCreating(true)}
            data-testid="seasonal-new-btn"
            className={`ml-auto h-10 px-3 bg-${brand}-600 hover:bg-${brand}-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1`}>
            <Plus size={14}/> New {tab === "snow" ? "Snow Route" : "Lawn Route"}
          </button>
          {tab === "snow" && <ScanSnowNowButton onDone={load}/>}
        </div>

        <div className="mt-4 border-b-2 border-slate-200 flex gap-1">
          <TabButton active={tab === "snow"} onClick={() => setTab("snow")} testid="seasonal-tab-snow">
            <Snowflake size={14}/> Snow Removal
          </TabButton>
          <TabButton active={tab === "lawn"} onClick={() => setTab("lawn")} testid="seasonal-tab-lawn">
            <Leaf size={14}/> Lawn Care
          </TabButton>
        </div>

        {loading && <div className="mt-8 text-center text-slate-500 font-bold" data-testid="seasonal-loading">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="mt-8 p-8 border-2 border-dashed border-slate-300 bg-slate-50 text-center" data-testid="seasonal-empty">
            <Icon size={44} className={`mx-auto mb-2 ${tab === "snow" ? "text-cyan-400" : "text-emerald-400"}`}/>
            <div className="font-black text-sm uppercase tracking-widest text-slate-600">
              No {tab === "snow" ? "snow routes" : "lawn routes"} configured yet
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Add {tab === "snow" ? "LCV or Lawn Guru" : "Lawn Guru or your in-house crew"} as a vendor and drop your covered properties in — we'll track the route + every visit.
            </div>
            <button onClick={() => setCreating(true)}
              data-testid="seasonal-empty-new"
              className={`mt-4 h-10 px-4 bg-${brand}-600 hover:bg-${brand}-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1`}>
              <Plus size={14}/> Create first route
            </button>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="mt-4 space-y-3">
            {filtered.map(s => (
              <ServiceCard key={s.id} s={s} brand={brand}
                expanded={expandedId === s.id}
                onExpand={() => setExpandedId(expandedId === s.id ? null : s.id)}
                onChanged={load}/>
            ))}
          </div>
        )}

        {creating && (
          <CreateServiceModal serviceType={tab} vendorsKnown={vendorsKnown}
            onClose={() => setCreating(false)}
            onCreated={() => { setCreating(false); load(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5 border-b-4 -mb-0.5 ${
        active ? "border-[#0f172a] text-[#0f172a]" : "border-transparent text-slate-500 hover:text-[#0f172a]"
      }`}>
      {children}
    </button>
  );
}

function ScanSnowNowButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/scan-now`, {},
        { headers: { Authorization: `Bearer ${token()}` } });
      const fired = data.fired || 0;
      const scanned = data.scanned || 0;
      if (fired > 0) {
        toast.success(`❄ ${fired} snow alert${fired === 1 ? "" : "s"} fired`);
      } else if (scanned > 0) {
        toast(`Scanned ${scanned} route${scanned === 1 ? "" : "s"} — nothing crossed threshold`);
      } else {
        toast("No active snow routes yet");
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Scan failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={scan} disabled={busy}
      data-testid="scan-snow-now-btn"
      className="h-10 px-3 border-2 border-cyan-700 bg-white hover:bg-cyan-50 text-cyan-900 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
      {busy ? "Scanning…" : "❄ Scan Now"}
    </button>
  );
}

/** Ensures a property_config exists for the address (idempotent), then
 *  routes into the full Property Portal. Powers the ↗ button on every
 *  seasonal coverage chip so all 53 lawn addresses are instantly clickable. */
function PortalOpenButton({ address, testid }) {
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const open = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/properties/ensure`, { address },
        { headers: { Authorization: `Bearer ${token()}` } });
      window.location.href = `/admin/properties/${encodeURIComponent(address)}`;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Failed to open portal"));
      setBusy(false);
    }
  };
  return (
    <button onClick={open} disabled={busy} data-testid={testid}
      title="Open Property Portal"
      className="px-1.5 border-l border-slate-300 hover:bg-fuchsia-600 hover:text-white text-slate-600 inline-flex items-center disabled:opacity-40">
      <ArrowSquareOut size={11} weight="bold"/>
    </button>
  );
}


function ServiceCard({ s, brand, expanded, onExpand, onChanged }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [editingCoverage, setEditingCoverage] = useState(false);
  const [coverageDraft, setCoverageDraft] = useState((s.coverage || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [visits, setVisits] = useState([]);
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [logTarget, setLogTarget] = useState(null);
  const [editingChip, setEditingChip] = useState(null);

  const saveCoverage = async () => {
    setBusy(true);
    try {
      const lines = coverageDraft.split("\n").map(x => x.trim()).filter(Boolean);
      await axios.patch(`${BASE}/api/seasonal-services/${s.id}`, { coverage: lines }, auth());
      toast.success("Coverage updated");
      setEditingCoverage(false);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const order = [...(s.route_order || [])];
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    try {
      await axios.patch(`${BASE}/api/seasonal-services/${s.id}`, { route_order: order }, auth());
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Reorder failed")); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${s.label || s.vendor_name}" route? Visit history will also be removed.`)) return;
    try {
      await axios.delete(`${BASE}/api/seasonal-services/${s.id}`, auth());
      toast.success("Deleted");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed")); }
  };

  const loadVisits = () => {
    axios.get(`${BASE}/api/seasonal-services/${s.id}/visits`, auth())
      .then(r => setVisits(r.data.visits || []))
      .catch(() => {});
  };

  const routeMap = {};
  (s.coverage || []).forEach((addr, i) => {
    const key = ((s.coverage_keys || [])[i]) || addr.toLowerCase().trim();
    routeMap[key] = addr;
  });

  return (
    <div className="rugged-card bg-white overflow-hidden" data-testid={`seasonal-card-${s.id}`}>
      <div className={`flex items-center gap-3 p-4 border-l-8 ${brand === "cyan" ? "border-cyan-500" : "border-emerald-500"}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-lg uppercase tracking-tight">
              {s.label || s.vendor_name}
            </span>
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${brand === "cyan" ? "bg-cyan-100 text-cyan-800" : "bg-emerald-100 text-emerald-800"}`}>
              {s.vendor_name}
            </span>
            {s.cadence && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">
                {s.cadence}
              </span>
            )}
            {!s.active && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-500 text-white">
                Inactive
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
            <span>{(s.coverage || []).length} properties covered</span>
            <span>· {s.visit_count} visits logged</span>
            {s.last_visit_at && <span>· last visit {new Date(s.last_visit_at).toLocaleDateString()}</span>}
            {s.monthly_fee_cents > 0 && <span>· ${(s.monthly_fee_cents/100).toFixed(2)}/mo</span>}
            {s.per_visit_fee_cents > 0 && <span>· ${(s.per_visit_fee_cents/100).toFixed(2)}/visit</span>}
          </div>
        </div>
        <button onClick={onExpand}
          data-testid={`seasonal-expand-${s.id}`}
          className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-slate-50">
          {expanded ? "Collapse" : "Manage"}
        </button>
        <button onClick={remove} data-testid={`seasonal-delete-${s.id}`}
          className="h-9 w-9 flex items-center justify-center text-red-600 hover:bg-red-50 border-2 border-transparent hover:border-red-500">
          <Trash size={14}/>
        </button>
      </div>

      {expanded && (
        <div className="border-t-2 border-slate-200 p-4 space-y-4 bg-slate-50">
          {/* Snow-only: Storm Watch panel — NWS forecast + draft alert.
              No emails/SMS ever fire from here; the draft is captured for
              review while EMAIL_KILLSWITCH is on. */}
          {s.service_type === "snow" && (
            <StormWatchPanel service={s} onChanged={onChanged}/>
          )}

          {/* Coverage */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={14}/>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Coverage</span>
              <BulkImportButton serviceId={s.id} onImported={onChanged}/>
              <button onClick={() => { setCoverageDraft((s.coverage || []).join("\n")); setEditingCoverage(v => !v); }}
                data-testid={`seasonal-edit-coverage-${s.id}`}
                className="h-7 px-2 text-[10px] font-black uppercase tracking-widest border-2 border-slate-300 hover:bg-white inline-flex items-center gap-1">
                <PencilSimple size={11}/> {editingCoverage ? "Cancel" : "Edit"}
              </button>
            </div>
            {!editingCoverage && (
              <div className="flex flex-wrap gap-1.5">
                {(s.coverage || []).length === 0
                  ? <span className="text-xs text-slate-500 font-bold">No addresses yet — tap Edit or Bulk Import.</span>
                  : (s.coverage || []).map((a, i) => {
                    const key = ((s.coverage_keys || [])[i]) || "";
                    const meta = (s.coverage_meta || {})[key] || {};
                    const vendor = meta.vendor || s.vendor_name;
                    const isLL = vendor && vendor.toLowerCase().includes("landlord");
                    return (
                      <div key={i} className={`inline-flex items-stretch border text-[11px] font-mono ${
                          meta.only_if_vacant ? "bg-amber-50 border-amber-400"
                          : isLL ? "bg-slate-100 border-slate-400"
                          : vendor === "LCV" ? "bg-cyan-50 border-cyan-400"
                          : "bg-white border-slate-300"
                        }`}>
                        <button data-testid={`seasonal-cov-${s.id}-${i}`}
                          onClick={() => setEditingChip({ address: a, key, meta: {...meta, vendor} })}
                          title={meta.notes || "click to edit vendor/frequency"}
                          className="px-2 py-1 inline-flex items-center gap-1 hover:bg-fuchsia-50">
                          {a}
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">· {vendor}</span>
                          {meta.frequency && <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">· {meta.frequency}</span>}
                          {meta.only_if_vacant && <span className="text-[9px] font-black uppercase tracking-widest text-amber-700">· vacant-only</span>}
                        </button>
                        <PortalOpenButton address={a} testid={`seasonal-portal-${s.id}-${i}`}/>
                      </div>
                    );
                  })
                }
              </div>
            )}
            {editingCoverage && (
              <div>
                <textarea value={coverageDraft} onChange={e => setCoverageDraft(e.target.value)}
                  rows={5}
                  placeholder={"5346 Chew Ave\n1730 W Somerset St\n..."}
                  data-testid={`seasonal-coverage-input-${s.id}`}
                  className="w-full px-3 py-2 border-2 border-slate-300 text-sm font-mono bg-white"/>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] text-slate-500">One address per line</span>
                  <button onClick={saveCoverage} disabled={busy}
                    data-testid={`seasonal-save-coverage-${s.id}`}
                    className={`ml-auto h-9 px-3 bg-${brand}-600 hover:bg-${brand}-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest`}>
                    {busy ? "Saving…" : "Save Coverage"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Route */}
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Route Order</span>
              <span className="text-[10px] text-slate-400">(reorder → ↑↓)</span>
              {s.route_total_miles > 0 && (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-800 text-white">
                  {s.route_total_miles} mi · ~{s.route_estimated_minutes} min
                </span>
              )}
              <RouteToolsBar service={s} onChanged={onChanged}/>
            </div>
            {(s.route_order || []).length === 0
              ? <div className="text-xs text-slate-500 font-bold">Add coverage above to build a route.</div>
              : (
                <div className="border-2 border-slate-200 bg-white divide-y divide-slate-200">
                  {(s.route_order || []).map((key, i) => {
                    const addr = routeMap[key] || key;
                    return (
                      <div key={key} data-testid={`seasonal-route-${s.id}-${i}`}
                        className="flex items-center gap-2 p-2">
                        <span className="w-6 h-6 flex items-center justify-center bg-slate-800 text-white text-xs font-black rounded-full">
                          {i + 1}
                        </span>
                        <span className="flex-1 text-sm font-bold truncate">{addr}</span>
                        <button onClick={() => move(i, -1)} disabled={i === 0}
                          className="h-7 w-7 flex items-center justify-center border border-slate-300 hover:bg-slate-100 disabled:opacity-30">
                          <CaretUp size={12}/>
                        </button>
                        <button onClick={() => move(i, +1)} disabled={i === (s.route_order || []).length - 1}
                          className="h-7 w-7 flex items-center justify-center border border-slate-300 hover:bg-slate-100 disabled:opacity-30">
                          <CaretDown size={12}/>
                        </button>
                        <button onClick={() => setLogTarget({ addr, seq: i + 1 })}
                          data-testid={`seasonal-log-visit-${s.id}-${i}`}
                          className={`h-7 px-2 bg-${brand}-600 hover:bg-${brand}-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1`}>
                          <Check size={11}/> Log visit
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>

          {/* Visits toggle */}
          <div>
            <button onClick={() => { const nx = !visitsOpen; setVisitsOpen(nx); if (nx) loadVisits(); }}
              data-testid={`seasonal-visits-toggle-${s.id}`}
              className="h-8 px-3 border-2 border-slate-300 hover:bg-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <ClockCounterClockwise size={12}/> {visitsOpen ? "Hide" : "Show"} recent visits
            </button>
            {visitsOpen && (
              <div className="mt-2 border-2 border-slate-200 bg-white max-h-64 overflow-y-auto">
                {visits.length === 0
                  ? <div className="p-3 text-xs text-slate-500 font-bold">No visits logged yet.</div>
                  : (
                    <div className="divide-y divide-slate-200">
                      {visits.map(v => (
                        <div key={v.id} className="p-2 text-xs" data-testid={`seasonal-visit-${v.id}`}>
                          <div className="font-bold">{v.address}</div>
                          <div className="text-[10px] text-slate-500">
                            {new Date(v.visited_at).toLocaleString()} · {v.visited_by || "—"}
                            {v.duration_minutes ? ` · ${v.duration_minutes} min` : ""}
                            {v.depth_inches ? ` · ${v.depth_inches}" snow` : ""}
                            {v.note ? ` · ${v.note}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }
              </div>
            )}
          </div>
        </div>
      )}
      {editingChip && (
        <CoverageChipEditor
          serviceId={s.id}
          address={editingChip.address}
          initial={editingChip.meta}
          onClose={() => setEditingChip(null)}
          onSaved={() => { setEditingChip(null); onChanged?.(); }}
        />
      )}
      {logTarget && (
        <LogVisitModal
          serviceId={s.id}
          serviceType={s.service_type}
          address={logTarget.addr}
          onClose={() => setLogTarget(null)}
          onLogged={() => { setLogTarget(null); loadVisits(); }}
        />
      )}
    </div>
  );
}

function CreateServiceModal({ serviceType, vendorsKnown, onClose, onCreated }) {
  const [form, setForm] = useState({
    vendor_name: vendorsKnown[0] || "LCV",
    label: "",
    coverage: "",
    cadence: serviceType === "snow" ? "on snow >1in" : "weekly",
    monthly_fee: "",
    per_visit_fee: "",
  });
  const [saving, setSaving] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const submit = async () => {
    if (!form.vendor_name.trim()) return toast.error("Vendor required");
    setSaving(true);
    try {
      const coverage = form.coverage.split("\n").map(x => x.trim()).filter(Boolean);
      await axios.post(`${BASE}/api/seasonal-services`, {
        service_type: serviceType,
        vendor_name: form.vendor_name.trim(),
        label: form.label.trim(),
        coverage,
        cadence: form.cadence.trim(),
        monthly_fee_cents: Math.round(parseFloat(form.monthly_fee || 0) * 100),
        per_visit_fee_cents: Math.round(parseFloat(form.per_visit_fee || 0) * 100),
        active: true,
      }, auth());
      toast.success("Route created");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Create failed")); }
    finally { setSaving(false); }
  };

  const label = serviceType === "snow" ? "Snow Route" : "Lawn Route";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="seasonal-create-modal">
      <div className="max-w-lg w-full bg-white border-2 border-[#0f172a] shadow-2xl">
        <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-slate-100">
          <div className="font-display font-black text-lg uppercase tracking-tight">New {label}</div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
            <X size={18}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Vendor</label>
              <input list="seasonal-vendors" value={form.vendor_name}
                onChange={e => setForm({...form, vendor_name: e.target.value})}
                data-testid="seasonal-create-vendor"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
              <datalist id="seasonal-vendors">
                {vendorsKnown.map(v => <option key={v} value={v}/>)}
              </datalist>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Nickname (optional)</label>
              <input value={form.label} onChange={e => setForm({...form, label: e.target.value})}
                placeholder="e.g. North Philly Route"
                data-testid="seasonal-create-label"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Coverage · one address per line</label>
            <textarea value={form.coverage} onChange={e => setForm({...form, coverage: e.target.value})}
              rows={5}
              placeholder={"5346 Chew Ave\n1730 W Somerset St\n2135 Chew Ave"}
              data-testid="seasonal-create-coverage"
              className="w-full px-3 py-2 border-2 border-slate-300 text-sm font-mono"/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Cadence</label>
              <input value={form.cadence} onChange={e => setForm({...form, cadence: e.target.value})}
                data-testid="seasonal-create-cadence"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">$/month</label>
              <input type="number" step="0.01" min="0"
                value={form.monthly_fee} onChange={e => setForm({...form, monthly_fee: e.target.value})}
                data-testid="seasonal-create-monthly"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">$/visit</label>
              <input type="number" step="0.01" min="0"
                value={form.per_visit_fee} onChange={e => setForm({...form, per_visit_fee: e.target.value})}
                data-testid="seasonal-create-per-visit"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
            </div>
          </div>
        </div>
        <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose}
            className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            data-testid="seasonal-create-submit"
            className="h-10 px-4 bg-[#0f172a] hover:bg-slate-800 disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest">
            {saving ? "Creating…" : "Create Route"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Storm Watch Panel ────────────────────────────────────────────────
// Fetches the NWS 7-day forecast for the service's region, highlights any
// period that crosses the alert threshold, and drafts a preview alert with
// the ordered route. NO emails/SMS ever fire — this is dry-run-only while
// the EMAIL_KILLSWITCH is on. When the switch goes off later, a dispatch
// endpoint will promote drafts to `queued`.
function StormWatchPanel({ service, onChanged }) {
  const [fx, setFx] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [viewingVisit, setViewingVisit] = useState(null);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${BASE}/api/seasonal-services/${service.id}/forecast`, auth()).catch(() => ({ data: null })),
      axios.get(`${BASE}/api/seasonal-services/${service.id}/storm-alerts`, auth()).catch(() => ({ data: { alerts: [] } })),
    ]).then(([f, a]) => {
      setFx(f.data);
      setAlerts(a.data?.alerts || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [service.id]);

  const draft = async (force = false) => {
    setDrafting(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${service.id}/storm-alert`,
        { force }, auth(),
      );
      if (!data.ok && data.reason === "no_storm_in_forecast") {
        toast("No storm ≥ threshold in the next 7 days");
      } else {
        toast.success(`Storm alert drafted (dry-run) · ${data.alert?.route_snapshot?.length || 0} stops`);
      }
      loadAll();
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Draft failed")); }
    finally { setDrafting(false); }
  };

  const [thrDraft, setThrDraft] = useState(String(service.snow_alert_threshold_in ?? 1));
  const [savingThr, setSavingThr] = useState(false);
  useEffect(() => { setThrDraft(String(service.snow_alert_threshold_in ?? 1)); }, [service.snow_alert_threshold_in]);
  const saveThr = async () => {
    const v = parseFloat(thrDraft);
    if (isNaN(v) || v < 0 || v > 48) { toast.error("Threshold must be 0–48 inches"); return; }
    setSavingThr(true);
    try {
      await axios.patch(`${BASE}/api/seasonal-services/${service.id}`,
        { snow_alert_threshold_in: v }, auth());
      toast.success(`Trigger set to ${v}"`);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSavingThr(false); }
  };

  const next = fx?.next_storm;
  const periods = fx?.periods || [];

  return (
    <div className="p-3 border-2 border-cyan-500 bg-cyan-50" data-testid={`storm-watch-${service.id}`}>
      <div className="flex items-center gap-2">
        <CloudSnow weight="fill" size={20} className="text-cyan-700"/>
        <div className="font-display font-black text-sm uppercase tracking-wider text-cyan-900">
          Storm Watch · NWS Forecast
        </div>
        <div className="flex items-center gap-1 text-[11px] font-bold text-cyan-900">
          <span>Alert ≥</span>
          <input type="number" min="0" max="48" step="0.1"
            value={thrDraft}
            onChange={e => setThrDraft(e.target.value)}
            onBlur={saveThr}
            onKeyDown={e => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
            data-testid={`storm-threshold-${service.id}`}
            disabled={savingThr}
            className="w-14 h-6 px-1 border-2 border-cyan-700 bg-white font-mono text-right rounded"/>
          <span>"</span>
        </div>
        <button onClick={loadAll} disabled={loading}
          data-testid={`storm-refresh-${service.id}`}
          className="ml-auto h-8 px-2 border-2 border-cyan-700 hover:bg-cyan-100 font-black uppercase text-[10px] tracking-widest">
          {loading ? "…" : "Refresh"}
        </button>
        <button onClick={() => draft(!next)} disabled={drafting}
          data-testid={`storm-draft-${service.id}`}
          className="h-8 px-3 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Warning size={12}/> {drafting ? "Drafting…" : (next ? "Draft Storm Alert" : "Force Draft")}
        </button>
      </div>

      {/* Kill-switch reminder banner — every draft is captured but nothing sends */}
      <div className="mt-2 p-2 border-2 border-red-500 bg-red-50 text-[11px] text-red-900 font-bold">
        Setup Mode · <b>no emails or SMS will fire from this panel</b>. Drafts are stored for review and preview only.
      </div>

      {fx?.error && (
        <div className="mt-2 text-[11px] text-amber-800 font-bold">
          NWS fetch: {fx.error}
        </div>
      )}

      {next && (
        <div className="mt-2 p-2 border-2 border-cyan-700 bg-white" data-testid={`storm-next-${service.id}`}>
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">
            Next storm crossing threshold
          </div>
          <div className="font-bold text-sm">{next.name} · ~{next.snow_inches_max || next.expected_inches}"</div>
          <div className="text-xs text-slate-600">{next.detailed || next.summary}</div>
        </div>
      )}

      {!next && periods.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-600 font-bold">
          No storm ≥ {service.snow_alert_threshold_in || 1}" in the next {periods.length} periods. Clear forecast.
        </div>
      )}

      {periods.length > 0 && (
        <details className="mt-2">
          <summary className="text-[10px] font-black uppercase tracking-widest text-cyan-800 cursor-pointer">
            7-day breakdown ({periods.length} periods)
          </summary>
          <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
            {periods.map((p, i) => (
              <div key={i} className={`p-1.5 text-[11px] flex items-center gap-2 ${p.will_trigger ? "bg-red-50 border border-red-300" : "bg-white border border-slate-200"}`}>
                <span className="font-black uppercase tracking-widest text-[9px] w-24 truncate">{p.name}</span>
                <span className="text-slate-500 text-[9px]">{p.temperature}°{p.temperature_unit}</span>
                {p.snow_inches_max > 0 && (
                  <span className={`px-1 py-0.5 text-[8px] font-black uppercase tracking-widest ${p.will_trigger ? "bg-red-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                    {p.snow_inches_max}" snow
                  </span>
                )}
                <span className="text-slate-600 truncate flex-1">{p.short}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {alerts.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 mb-1">
            Drafted Alerts ({alerts.length})
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 5).map(a => (
              <div key={a.id} className={`p-2 border-2 bg-white ${a.kind === "refreeze" ? "border-orange-400" : "border-slate-300"}`}
                data-testid={`storm-alert-${a.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${a.kind === "refreeze" ? "bg-orange-600 text-white" : "bg-slate-800 text-white"}`}>
                    {a.kind === "refreeze" ? "❄ REFREEZE" : "STORM"}
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-slate-500 text-white px-1.5 py-0.5">
                    {a.status}
                  </span>
                  {a.killswitch_blocked && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5">
                      Kill switch blocked send
                    </span>
                  )}
                  {a.kind === "refreeze" && a.low_temp_f !== null && a.low_temp_f !== undefined && (
                    <span className="text-[10px] font-bold text-orange-800">
                      Treated {a.treated_at_temp_f}°F → drop to {a.low_temp_f}°F within 6h
                    </span>
                  )}
                  {a.kind !== "refreeze" && (
                    <span className="text-[10px] text-slate-500">
                      ~{a.expected_inches}" · {a.route_snapshot?.length || 0} stops
                    </span>
                  )}
                  {a.visit && (
                    <button onClick={() => setViewingVisit({ alert: a, visit: a.visit })}
                      data-testid={`view-visit-${a.id}`}
                      className="ml-auto h-6 px-2 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-[9px] tracking-widest">
                      View Treatment →
                    </button>
                  )}
                </div>
                <pre className="text-[10px] font-mono whitespace-pre-wrap bg-slate-50 p-2 border border-slate-200 max-h-32 overflow-y-auto">
{a.message_preview}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
      {viewingVisit && (
        <RefreezeVisitModal
          serviceId={service.id}
          alert={viewingVisit.alert}
          visit={viewingVisit.visit}
          onClose={() => setViewingVisit(null)}
        />
      )}
    </div>
  );
}

// ─── Bulk Import Button (Grace's PMR spreadsheet paste) ───────────────
// Parses a TSV/CSV-like paste of the ProCall/PMR annual property list into
// the /coverage-bulk endpoint. Expected columns (any order, tab or comma):
// address · frequency · status · notes. Rows with "only if vacant (sfh)" in
// the frequency column are auto-flagged.
function BulkImportButton({ serviceId, onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const parse = () => {
    const rows = text.split("\n").map(l => l.trim()).filter(Boolean);
    return rows.map(line => {
      // Support both tab-separated (spreadsheet paste) and comma-separated.
      const parts = line.includes("\t") ? line.split("\t") : line.split(",");
      const [address, frequency, status, notes] = parts.map(p => (p || "").trim());
      const freqLower = (frequency || "").toLowerCase();
      return {
        address: address || "",
        frequency: freqLower,
        only_if_vacant: freqLower.includes("only if vacant") || freqLower === "sfh",
        status: (status || "").toLowerCase(),
        notes: notes || "",
      };
    }).filter(e => e.address.length > 3);
  };

  const submit = async () => {
    const entries = parse();
    if (!entries.length) return toast.error("No addresses parsed — paste one address per line");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${serviceId}/coverage-bulk`,
        { entries, replace }, auth(),
      );
      toast.success(`Imported ${data.imported} · ${data.new_addresses} new · ${data.total_coverage} total`);
      setOpen(false);
      setText("");
      onImported?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        data-testid={`bulk-import-btn-${serviceId}`}
        className="ml-auto h-7 px-2 border-2 border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
        <UploadSimple size={11}/> Bulk Import
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white border-2 border-[#0f172a] shadow-2xl">
            <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-fuchsia-50">
              <div className="font-display font-black text-lg uppercase tracking-tight">Bulk Import Coverage</div>
              <button onClick={() => setOpen(false)} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
                <X size={18}/>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-slate-700">
                Paste from Grace's PMR spreadsheet. One row per line, columns:{" "}
                <b>Address · Frequency · Status · Notes</b>. Separated by tab (Excel/Google Sheets paste) or comma.
              </div>
              <div className="text-[10px] font-mono bg-slate-50 border border-slate-300 p-2">
                {`5346 Chew Ave\tweekly\toccupied\tBushes/Shrubs\n6032 Walnut St\tonly if vacant (sfh)\tvacant\tStop 5/09/2026`}
              </div>
              <textarea value={text} onChange={e => setText(e.target.value)}
                rows={12}
                data-testid="bulk-import-textarea"
                placeholder="Paste rows here…"
                className="w-full px-3 py-2 border-2 border-slate-300 text-sm font-mono"/>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)}
                  data-testid="bulk-import-replace"/>
                <span><b>Replace</b> — remove existing coverage first (otherwise merge in)</span>
              </label>
            </div>
            <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
              <button onClick={() => setOpen(false)}
                className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
                Cancel
              </button>
              <button onClick={submit} disabled={busy}
                data-testid="bulk-import-submit"
                className="h-10 px-4 bg-fuchsia-700 hover:bg-fuchsia-800 disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest">
                {busy ? "Importing…" : `Import ${parse().length} rows`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── Log Visit Modal (Salt Depth Log) ─────────────────────────────────
// Replaces the tiny "Log visit" button. For snow visits this captures the
// full defensible record: snow depth, salt applied + bags + product, and
// ambient temp (auto-fills from NWS if left blank). For lawn visits it's
// just duration + notes. Photo path is optional and stored as a text field
// for now — a real uploader can slot in later.
function LogVisitModal({ serviceId, serviceType, address, onClose, onLogged }) {
  const isSnow = serviceType === "snow";
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const [form, setForm] = useState({
    visited_by: "",           // techs picker; blank → server uses logged-in admin
    visited_at: today,        // date picker (YYYY-MM-DD)
    depth_inches: "",
    duration_minutes: "",
    salt_applied: false,
    salt_bags: "",
    salt_product: "rock salt",
    ambient_temp_f: "",   // blank = autofill from NWS server-side
    photo_path: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [techs, setTechs] = useState([]);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    axios.get(`${BASE}/api/assignable-users`, auth())
      .then(r => setTechs(r.data || []))
      .catch(() => setTechs([]));
    /* eslint-disable-next-line */
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        address,
        note: form.note || "",
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : null,
        visited_by: form.visited_by || null,
        visited_at: form.visited_at || null,
      };
      if (isSnow) {
        if (form.depth_inches) payload.depth_inches = parseFloat(form.depth_inches);
        payload.salt_applied = !!form.salt_applied;
        if (form.salt_applied) {
          if (form.salt_bags) payload.salt_bags = parseFloat(form.salt_bags);
          if (form.salt_product) payload.salt_product = form.salt_product;
        }
        if (form.ambient_temp_f !== "") payload.ambient_temp_f = parseFloat(form.ambient_temp_f);
        if (form.photo_path) payload.photo_path = form.photo_path;
      }
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${serviceId}/visits`, payload, auth(),
      );
      const parts = [`Visit logged for ${address}`];
      if (data.ambient_temp_f !== null && data.ambient_temp_f !== undefined) {
        parts.push(`${data.ambient_temp_f}°F` + (data.ambient_temp_source === "nws" ? " (NWS)" : ""));
      }
      if (data.salt_applied) parts.push(`${data.salt_bags || "?"} bg ${data.salt_product || "salt"}`);
      toast.success(parts.join(" · "));
      if (data.refreeze_alert) {
        // Prominent secondary toast so the PM sees the automatic follow-up
        // right after logging — encourages them to review the draft.
        toast.warning(
          `Refreeze predicted → follow-up alert drafted (${data.refreeze_alert.low_temp_f}°F within 6h)`,
          { duration: 8000 },
        );
      }
      onLogged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Log failed")); }
    finally { setSaving(false); }
  };

  const coldWarn = form.salt_applied && form.salt_product === "rock salt"
                   && form.ambient_temp_f !== "" && parseFloat(form.ambient_temp_f) < 15;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="log-visit-modal">
      <div className="max-w-md w-full bg-white border-2 border-[#0f172a] shadow-2xl">
        <div className={`px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between ${isSnow ? "bg-cyan-50" : "bg-emerald-50"}`}>
          <div>
            <div className="font-display font-black text-lg uppercase tracking-tight">
              Log {isSnow ? "Snow" : "Lawn"} Visit
            </div>
            <div className="text-xs font-mono text-slate-700 truncate max-w-xs">{address}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
            <X size={18}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Who + When — asked-for by user */}
          <div className="grid grid-cols-2 gap-3 p-3 border-2 border-slate-900 bg-slate-50">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Who did it?</label>
              <select value={form.visited_by}
                onChange={e => setForm({...form, visited_by: e.target.value})}
                data-testid="visit-tech-select"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm bg-white">
                <option value="">— Me (default) —</option>
                {techs.map(t => (
                  <option key={t.id} value={t.name || t.email}>
                    {t.name || t.email}{t.role === "worker" ? "" : ` · ${t.role}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">When?</label>
              <input type="date" value={form.visited_at}
                onChange={e => setForm({...form, visited_at: e.target.value})}
                data-testid="visit-date-input"
                max={today}
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm bg-white"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {isSnow && (
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Snow depth (in)</label>
                <input type="number" step="0.1" min="0"
                  value={form.depth_inches} onChange={e => setForm({...form, depth_inches: e.target.value})}
                  data-testid="visit-depth-input"
                  placeholder="e.g. 3.5"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
              </div>
            )}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Duration (min)</label>
              <input type="number" min="0" max="1440"
                value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value})}
                data-testid="visit-duration-input"
                placeholder="e.g. 45"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
            </div>
          </div>

          {isSnow && (
            <div className="p-3 border-2 border-cyan-500 bg-cyan-50 space-y-3" data-testid="salt-depth-log">
              <label className="flex items-center gap-2 text-sm font-bold text-cyan-900">
                <input type="checkbox" checked={form.salt_applied}
                  onChange={e => setForm({...form, salt_applied: e.target.checked})}
                  data-testid="visit-salt-applied"/>
                Salt applied at this stop
              </label>
              {form.salt_applied && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">Bags (50 lb)</label>
                    <input type="number" step="0.5" min="0"
                      value={form.salt_bags} onChange={e => setForm({...form, salt_bags: e.target.value})}
                      data-testid="visit-salt-bags"
                      placeholder="e.g. 2"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">Product</label>
                    <select value={form.salt_product}
                      onChange={e => setForm({...form, salt_product: e.target.value})}
                      data-testid="visit-salt-product"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
                      <option value="rock salt">Rock salt</option>
                      <option value="calcium chloride">Calcium chloride</option>
                      <option value="magnesium chloride">Magnesium chloride</option>
                      <option value="sand mix">Sand mix</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">
                  Ambient temp (°F) <span className="text-slate-400 normal-case font-normal">— leave blank to auto-fill from NWS</span>
                </label>
                <input type="number" step="0.5" min="-30" max="130"
                  value={form.ambient_temp_f} onChange={e => setForm({...form, ambient_temp_f: e.target.value})}
                  data-testid="visit-temp"
                  placeholder="e.g. 18"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                {coldWarn && (
                  <div className="mt-1 text-[11px] text-red-700 font-bold">
                    ⚠ Rock salt is ineffective below 15°F — recommend calcium chloride for insurance defensibility.
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">Photo URL (optional)</label>
                <input value={form.photo_path} onChange={e => setForm({...form, photo_path: e.target.value})}
                  data-testid="visit-photo-path"
                  placeholder="Paste an image URL, or leave blank"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Notes</label>
            <textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})}
              data-testid="visit-note"
              placeholder="e.g. treated stoop + walkway, refreeze possible overnight"
              className="w-full px-2 py-2 border-2 border-slate-300 text-sm"/>
          </div>
        </div>
        <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose}
            className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            data-testid="visit-submit"
            className={`h-10 px-4 ${isSnow ? "bg-cyan-700 hover:bg-cyan-800" : "bg-emerald-700 hover:bg-emerald-800"} disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest`}>
            {saving ? "Saving…" : "Log Visit"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Visit Photo Picker (real object-storage backed) ──────────────────
// Replaces the old "paste an image URL" text field. Uses the native file
// picker with `capture="environment"` so mobile browsers open the back
// camera by default. Uploads to /visit-photo → returns a storage path we
// hand back to the parent form. Thumbnail preview after upload; click ×
// to remove and pick again.
function VisitPhotoPicker({ serviceId, photoPath, onChange }) {
  const [uploading, setUploading] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Pick an image file");
    if (f.size > 3 * 1024 * 1024) return toast.error("Image too large — 3 MB max");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${serviceId}/visit-photo`, fd,
        { headers: { ...auth().headers, "Content-Type": "multipart/form-data" } },
      );
      onChange?.(data.path);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Upload failed")); }
    finally { setUploading(false); }
  };

  const streamUrl = photoPath
    ? `${BASE}/api/seasonal-services/visit-photo/${photoPath}`
    : "";

  if (photoPath) {
    return (
      <div className="relative inline-block" data-testid="visit-photo-preview">
        <img src={streamUrl}
          alt="Treatment"
          className="w-32 h-32 object-cover border-2 border-slate-400"/>
        <button onClick={() => onChange?.("")}
          data-testid="visit-photo-remove"
          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white border-2 border-white rounded-full">
          <X size={12}/>
        </button>
      </div>
    );
  }
  return (
    <label className="block h-24 border-2 border-dashed border-slate-400 hover:border-cyan-500 hover:bg-cyan-50 cursor-pointer flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest text-slate-600"
      data-testid="visit-photo-picker">
      {uploading ? "Uploading…" : "📷 Tap to capture / pick photo"}
      <input type="file" accept="image/*" capture="environment"
        onChange={onPick} disabled={uploading}
        data-testid="visit-photo-input"
        className="hidden"/>
    </label>
  );
}

// ─── Route Tools Bar (Optimize · Navigate · Print) ────────────────────
// Replaces third-party route planner subscriptions like RoadWarrior /
// Route4Me / Circuit. One click to geocode + optimize the route, one
// click to hand it to Google Maps on the vendor's phone, one click to
// print a numbered driver sheet with salt-log checkboxes.
function RouteToolsBar({ service, onChanged }) {
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [origin, setOrigin] = useState(service.route_origin_address || "");
  const [driverCount, setDriverCount] = useState(service.driver_count || 2);
  const [driverLabels, setDriverLabels] = useState(
    (service.driver_routes || []).map(d => d.driver_label) || []
  );
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const optimize = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${service.id}/optimize-route`,
        { origin_address: origin, apply: true }, auth(),
      );
      const parts = [`${data.stops} stops · ${data.total_miles}mi · ~${data.estimated_minutes}min`];
      if ((data.failed_to_geocode || []).length) {
        parts.push(`${data.failed_to_geocode.length} couldn't geocode`);
      }
      toast.success(`Route optimized · ${parts.join(" · ")}`);
      setOptimizeOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Optimize failed")); }
    finally { setBusy(false); }
  };

  const openNav = async (driver = "google", driver_index = undefined) => {
    try {
      const params = { driver };
      if (driver_index !== undefined) params.driver_index = driver_index;
      const { data } = await axios.get(
        `${BASE}/api/seasonal-services/${service.id}/nav-url`,
        { ...auth(), params },
      );
      if (data.chunk_count > 1) {
        toast(`${data.total_stops} stops split into ${data.chunk_count} Google Maps links — opening all`);
        for (const url of data.chunks) window.open(url, "_blank", "noopener");
      } else {
        window.open(data.url, "_blank", "noopener");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Nav link failed")); }
  };

  const printSheet = (driver_index = undefined) => {
    const url = `${BASE}/api/seasonal-services/${service.id}/route-sheet.pdf`
                + (driver_index !== undefined ? `?driver_index=${driver_index}` : "");
    (async () => {
      try {
        const r = await axios.get(url, { ...auth(), responseType: "blob" });
        const blob = new Blob([r.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "_blank", "noopener");
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "PDF failed")); }
    })();
  };

  const doSplit = async () => {
    setBusy(true);
    try {
      const labels = Array.from({ length: driverCount }, (_, i) => driverLabels[i] || `Truck ${i + 1}`);
      const { data } = await axios.post(
        `${BASE}/api/seasonal-services/${service.id}/split-route`,
        { driver_count: driverCount, driver_labels: labels,
          origin_address: origin, apply: true },
        auth(),
      );
      toast.success(`Split into ${data.driver_count} routes · ${data.combined_miles}mi total`);
      setSplitOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Split failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="ml-auto flex items-center gap-1.5 flex-wrap">
        <button onClick={() => setOptimizeOpen(true)}
          data-testid={`route-optimize-${service.id}`}
          className="h-7 px-2 border-2 border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Path size={11}/> Optimize
        </button>
        <button onClick={() => setSplitOpen(true)}
          data-testid={`route-split-${service.id}`}
          className="h-7 px-2 border-2 border-purple-500 hover:bg-purple-50 text-purple-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Path size={11}/> Split ({(service.driver_routes || []).length || "Multi"})
        </button>
        <button onClick={() => openNav("google")}
          data-testid={`route-nav-google-${service.id}`}
          className="h-7 px-2 border-2 border-blue-500 hover:bg-blue-50 text-blue-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <NavigationArrow size={11}/> Nav
        </button>
        <button onClick={() => printSheet()}
          data-testid={`route-print-${service.id}`}
          className="h-7 px-2 border-2 border-slate-500 hover:bg-slate-100 text-slate-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Printer size={11}/> Print
        </button>
      </div>

      {/* Per-driver sub-routes surface — only shown when a split has been saved */}
      {(service.driver_routes || []).length > 0 && (
        <div className="w-full mt-2 space-y-1" data-testid={`driver-routes-${service.id}`}>
          <div className="text-[10px] font-black uppercase tracking-widest text-purple-800">
            Split into {service.driver_routes.length} drivers
          </div>
          {service.driver_routes.map(dr => (
            <div key={dr.driver_index} className="flex items-center gap-2 p-2 bg-purple-50 border border-purple-200"
              data-testid={`driver-route-${service.id}-${dr.driver_index}`}>
              <span className="text-[10px] font-black uppercase tracking-widest bg-purple-700 text-white px-1.5 py-0.5">
                {dr.driver_label}
              </span>
              <span className="text-xs font-bold text-slate-700">
                {dr.stops} stops · {dr.total_miles}mi · ~{dr.estimated_minutes}min
              </span>
              <button onClick={() => openNav("google", dr.driver_index)}
                data-testid={`driver-nav-${service.id}-${dr.driver_index}`}
                className="ml-auto h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
                <NavigationArrow size={11}/> Nav
              </button>
              <button onClick={() => printSheet(dr.driver_index)}
                data-testid={`driver-print-${service.id}-${dr.driver_index}`}
                className="h-7 px-2 bg-slate-700 hover:bg-slate-800 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
                <Printer size={11}/> Print
              </button>
            </div>
          ))}
        </div>
      )}
      {optimizeOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="route-optimize-modal">
          <div className="max-w-md w-full bg-white border-2 border-[#0f172a] shadow-2xl">
            <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-emerald-50">
              <div className="font-display font-black text-lg uppercase tracking-tight">Optimize Route</div>
              <button onClick={() => setOptimizeOpen(false)} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
                <X size={18}/>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                  Starting point (optional)
                </label>
                <input value={origin} onChange={e => setOrigin(e.target.value)}
                  data-testid="route-origin-input"
                  placeholder="e.g. 1234 Vendor Yard, Philadelphia PA"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                <div className="mt-1 text-[11px] text-slate-500">
                  LCV's yard or your office. Leave blank to start from the first stop.
                </div>
              </div>
              <div className="text-[11px] text-slate-600">
                We'll geocode every stop (free OpenStreetMap, cached), run nearest-neighbor TSP, and save the shortest order. Typically 15–25% fewer drive miles vs random.
              </div>
            </div>
            <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
              <button onClick={() => setOptimizeOpen(false)}
                className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
                Cancel
              </button>
              <button onClick={optimize} disabled={busy}
                data-testid="route-optimize-submit"
                className="h-10 px-4 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest">
                {busy ? "Optimizing…" : "Optimize & Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── Refreeze Visit Modal ─────────────────────────────────────────────
// Opened from a refreeze alert in Storm Watch. Renders the treated photo
// + salt bags + product + ambient temp side-by-side with the refreeze
// prediction so discovery-day evidence sits in one screenshot.
function RefreezeVisitModal({ serviceId, alert, visit, onClose }) {
  const photoUrl = visit.photo_path
    ? `${BASE}/api/seasonal-services/visit-photo/${visit.photo_path}`
    : "";
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="refreeze-visit-modal">
      <div className="max-w-3xl w-full bg-white border-2 border-orange-500 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b-2 border-orange-500 flex items-center justify-between bg-orange-50">
          <div>
            <div className="font-display font-black text-lg uppercase tracking-tight">
              ❄ Refreeze — Treatment Evidence
            </div>
            <div className="text-xs font-mono text-slate-700 truncate max-w-lg">{alert.address}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center hover:bg-orange-100">
            <X size={18}/>
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: treatment record */}
          <div className="space-y-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Treatment Record</div>
            {photoUrl ? (
              <img src={photoUrl} alt="Treated walkway"
                data-testid="refreeze-visit-photo"
                className="w-full h-64 object-cover border-2 border-slate-400"/>
            ) : (
              <div className="w-full h-64 bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-xs font-bold text-slate-500">
                No photo captured
              </div>
            )}
            <dl className="text-xs space-y-1">
              <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Visited</dt>
                <dd className="font-mono">{new Date(visit.visited_at).toLocaleString()}</dd></div>
              <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">By</dt>
                <dd>{visit.visited_by || "—"}</dd></div>
              {visit.depth_inches !== null && visit.depth_inches !== undefined && (
                <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Snow depth</dt>
                  <dd>{visit.depth_inches}"</dd></div>
              )}
              {visit.salt_applied && (
                <>
                  <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Salt bags</dt>
                    <dd className="font-black">{visit.salt_bags || "?"}</dd></div>
                  <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Product</dt>
                    <dd>{visit.salt_product || "—"}</dd></div>
                </>
              )}
              {visit.ambient_temp_f !== null && visit.ambient_temp_f !== undefined && (
                <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Treated at</dt>
                  <dd className="font-black text-cyan-800">{visit.ambient_temp_f}°F{visit.ambient_temp_source === "nws" ? " (NWS)" : ""}</dd></div>
              )}
              {visit.duration_minutes && (
                <div className="flex gap-2"><dt className="w-24 font-black uppercase tracking-widest text-[9px] text-slate-500">Duration</dt>
                  <dd>{visit.duration_minutes} min</dd></div>
              )}
              {visit.note && (
                <div className="mt-2 p-2 bg-slate-50 border border-slate-200 text-slate-700">{visit.note}</div>
              )}
            </dl>
          </div>
          {/* Right: refreeze prediction */}
          <div className="space-y-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-orange-800">Refreeze Prediction</div>
            <div className="p-3 border-2 border-orange-500 bg-orange-50">
              <div className="text-3xl font-display font-black text-orange-800">
                {alert.low_temp_f}°F
              </div>
              <div className="text-[11px] text-orange-900 font-bold">
                Low expected within 6h of treatment
              </div>
            </div>
            <div className="p-3 border-2 border-slate-300 bg-slate-50 text-xs text-slate-700">
              {alert.storm_summary}
            </div>
            <pre className="text-[10px] font-mono whitespace-pre-wrap bg-slate-900 text-slate-100 p-2 max-h-48 overflow-y-auto">
{alert.message_preview}
            </pre>
            <div className="text-[11px] text-red-800 font-bold p-2 border-2 border-red-400 bg-red-50">
              {alert.killswitch_blocked
                ? "Setup mode active — this alert was NOT sent. Preview only."
                : `Would send to: ${alert.would_send_to}`}
            </div>
          </div>
        </div>
        <div className="p-3 border-t-2 border-orange-500 flex justify-end bg-slate-50">
          <button onClick={onClose}
            data-testid="refreeze-modal-close"
            className="h-10 px-4 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase text-[11px] tracking-widest">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Coverage Chip Editor ────────────────────────────────────────────
// Click any property chip in the coverage grid → this modal opens with
// vendor dropdown (Lawn Guru / LCV / Landlord's Contractor / custom),
// frequency, only-if-vacant, status, and notes. One-click save patches
// just that address's coverage_meta.
function CoverageChipEditor({ serviceId, address, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor: initial?.vendor || "Lawn Guru",
    frequency: initial?.frequency || "",
    only_if_vacant: !!initial?.only_if_vacant,
    status: initial?.status || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const submit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${BASE}/api/seasonal-services/${serviceId}/coverage-meta`,
        { address, ...form }, auth());
      toast.success(`Saved · ${address} → ${form.vendor}`);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="chip-editor-modal">
      <div className="max-w-md w-full bg-white border-2 border-[#0f172a] shadow-2xl">
        <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-fuchsia-50">
          <div>
            <div className="font-display font-black text-lg uppercase tracking-tight">Edit Property</div>
            <div className="text-xs font-mono text-slate-700 truncate">{address}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
            <X size={18}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Vendor / Provider</label>
            <select value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})}
              data-testid="chip-vendor-select"
              className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
              <option value="Lawn Guru">Lawn Guru</option>
              <option value="LCV">LCV</option>
              <option value="Landlord's Contractor">Landlord's Contractor (tracking only)</option>
              <option value="In-House">In-House</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Frequency</label>
              <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}
                data-testid="chip-frequency-select"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
                <option value="">—</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="on_demand">On demand</option>
                <option value="only_if_vacant">Only if vacant (SFH)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                data-testid="chip-status-select"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
                <option value="">—</option>
                <option value="occupied">Occupied</option>
                <option value="vacant">Vacant</option>
                <option value="unit_1_vacant">Unit 1 Vacant</option>
                <option value="unit_1_commercial_vacant">Unit 1 Commercial Vacant</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.only_if_vacant}
              onChange={e => setForm({...form, only_if_vacant: e.target.checked})}
              data-testid="chip-only-if-vacant"/>
            <span>Service only when vacant (SFH)</span>
          </label>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Notes</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              data-testid="chip-notes-input"
              placeholder="e.g. Bushes/Shrubs cleanup, Discrepancy Review, Stop 5/09/2026…"
              className="w-full px-2 py-2 border-2 border-slate-300 text-sm"/>
          </div>
        </div>
        <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose}
            className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
            data-testid="chip-save"
            className="h-10 px-4 bg-fuchsia-700 hover:bg-fuchsia-800 disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

